/**
 * Fiche produit vitrine TEXTIPRINT (Sprint 3 MVP).
 * Brief SIM-PLATFORM-BOUTIQUE-2026-001 §2.6 (page fiche produit).
 *
 * Affiche : galerie photos · nom · marque · description · compatibilités ·
 * remises volume · bouton "Commander via WhatsApp" avec message pré-rempli.
 *
 * Pas de panier MVP — la commande passe directement par WhatsApp avec
 * le numéro TEXTIPRINT (cf. brief §3.4 — 80% des commandes CI = WhatsApp).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Sparkles, Truck, CheckCircle2 } from 'lucide-react';
import { vitrineApi, type VitrineProduit } from '@/api/textprint-public';

// Numéro WhatsApp TEXTIPRINT — format international WhatsApp (sans +, sans espaces)
// CI +225 07 67 07 39 76 → 2250767073976
const WHATSAPP_NUMBER = '2250767073976';

const formatFCFA = (cents: string | number): string => {
  const value = typeof cents === 'string' ? Number(BigInt(cents)) : Number(cents);
  return new Intl.NumberFormat('fr-FR').format(Math.round(value / 100));
};

export const VitrineProduitPage = () => {
  const { sku } = useParams<{ sku: string }>();
  const navigate = useNavigate();
  const [produit, setProduit] = useState<VitrineProduit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const [varianteSel, setVarianteSel] = useState<string>('');

  useEffect(() => {
    if (!sku) return;
    setLoading(true);
    void (async () => {
      try {
        const p = await vitrineApi.produit(sku);
        setProduit(p);
        // Quantité initiale = MOQ si personnalisable
        if (p.personnalisable && p.quantiteMinimum) setQty(p.quantiteMinimum);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [sku]);

  if (loading) return <div className="t-textprint" style={{ minHeight: '100vh', background: 'var(--bg)', padding: 40, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>;
  if (error || !produit) return (
    <div className="t-textprint" style={{ minHeight: '100vh', background: 'var(--bg)', padding: 40 }}>
      <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
        <h2 style={{ fontFamily: 'var(--font-d)', color: 'var(--tx)' }}>Produit introuvable</h2>
        <p style={{ color: 'var(--tx-3)' }}>{error}</p>
        <button onClick={() => navigate('/vitrine')} className="ds-btn ds-btn-p" style={{ padding: '10px 20px', background: 'var(--p)', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
          ← Retour catalogue
        </button>
      </div>
    </div>
  );

  // Calcul prix avec remise volume éventuelle
  const prixUnitCents = Number(BigInt(produit.prixUnitaireHtCents));
  const remiseApplicable = (produit.remisesVolume ?? [])
    .filter((r) => qty >= r.quantiteMin)
    .reduce((max, r) => (Number(r.remisePct) > max ? Number(r.remisePct) : max), 0);
  const prixApresRemiseCents = prixUnitCents * (1 - remiseApplicable / 100);
  const totalHtCents = prixApresRemiseCents * qty;
  const setupCents = produit.prixSetupCents ? Number(BigInt(produit.prixSetupCents)) : 0;
  const grandTotalCents = totalHtCents + setupCents;

  // Construction message WhatsApp
  const messageLines = [
    `Bonjour TEXTIPRINT 🖨️`,
    ``,
    `Je souhaite commander :`,
    `• Produit : ${produit.nom}`,
    `• Référence : ${produit.sku}`,
    `• Quantité : ${qty} ${produit.unite}`,
  ];
  if (varianteSel) {
    const v = produit.variantes?.find((x) => x.id === varianteSel);
    if (v) messageLines.push(`• Variante : ${[v.taille, v.couleur].filter(Boolean).join(' / ')}`);
  }
  if (remiseApplicable > 0) messageLines.push(`• Remise volume : -${remiseApplicable}%`);
  messageLines.push(``);
  messageLines.push(`Prix unitaire : ${formatFCFA(prixUnitCents)} FCFA HT`);
  if (remiseApplicable > 0) messageLines.push(`Prix après remise : ${formatFCFA(prixApresRemiseCents)} FCFA HT`);
  messageLines.push(`Sous-total : ${formatFCFA(totalHtCents)} FCFA HT`);
  if (setupCents > 0) messageLines.push(`Frais setup : ${formatFCFA(setupCents)} FCFA HT`);
  messageLines.push(`Total estimé : ${formatFCFA(grandTotalCents)} FCFA HT`);
  messageLines.push(``);
  messageLines.push(`Merci de me confirmer la disponibilité et le délai. 🙏`);

  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(messageLines.join('\n'))}`;

  return (
    <div className="t-textprint" style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header style={{ background: 'linear-gradient(135deg, var(--p-deep), var(--p-dark))', color: 'white', padding: '20px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <button
            onClick={() => navigate('/vitrine')}
            style={{ background: 'rgba(255,255,255,.15)', border: 'none', color: 'white', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
          >
            <ArrowLeft size={14} /> Catalogue
          </button>
        </div>
      </header>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24, display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) 1fr', gap: 32 }}>
        {/* Galerie */}
        <div>
          <div style={{
            aspectRatio: '1', background: produit.photos[0]
              ? `url(${produit.photos[0]}) center/cover no-repeat`
              : 'linear-gradient(135deg, var(--p-soft), var(--bg))',
            borderRadius: 12, border: '1px solid var(--bdr)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--p)',
          }}>
            {!produit.photos[0] && <div style={{ textAlign: 'center' }}><Sparkles size={48} /><div style={{ marginTop: 8, fontFamily: 'var(--font-d)' }}>{produit.famille?.nom}</div></div>}
          </div>
        </div>

        {/* Détail */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            {produit.famille && <div style={{ fontSize: 11, color: 'var(--tx-3)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{produit.famille.nom}</div>}
            <h1 style={{ fontFamily: 'var(--font-d)', fontSize: 26, color: 'var(--tx)', margin: '4px 0', lineHeight: 1.2 }}>{produit.nom}</h1>
            <div style={{ fontSize: 12, color: 'var(--tx-3)' }}>
              SKU <code style={{ background: 'var(--surface-2)', padding: '1px 6px', borderRadius: 3 }}>{produit.sku}</code>
              {produit.marque && <> · Marque <strong>{produit.marque}</strong></>}
              {produit.refConstructeur && <> · Réf <code style={{ background: 'var(--surface-2)', padding: '1px 6px', borderRadius: 3 }}>{produit.refConstructeur}</code></>}
            </div>
          </div>

          {produit.description && (
            <p style={{ fontSize: 14, color: 'var(--tx-2)', lineHeight: 1.5 }}>{produit.description}</p>
          )}

          {produit.compatibilites.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 6 }}>Compatible avec :</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {produit.compatibilites.slice(0, 10).map((c, i) => (
                  <span key={i} style={{ background: 'var(--p-soft)', color: 'var(--p)', padding: '4px 10px', borderRadius: 4, fontSize: 11 }}>{c}</span>
                ))}
              </div>
            </div>
          )}

          {/* Variantes */}
          {(produit.variantes ?? []).length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 6 }}>Variante :</div>
              <select value={varianteSel} onChange={(e) => setVarianteSel(e.target.value)} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--bdr)', fontSize: 13, background: 'white', color: 'var(--tx)' }}>
                <option value="">Choisir une variante…</option>
                {produit.variantes!.map((v) => (
                  <option key={v.id} value={v.id}>{[v.taille, v.couleur].filter(Boolean).join(' / ')}</option>
                ))}
              </select>
            </div>
          )}

          {/* Quantité */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 6 }}>
              Quantité {produit.quantiteMinimum && `(min ${produit.quantiteMinimum})`} :
            </div>
            <input
              type="number" min={produit.quantiteMinimum ?? 1} value={qty}
              onChange={(e) => setQty(Math.max(produit.quantiteMinimum ?? 1, parseInt(e.target.value) || 1))}
              style={{ width: 120, padding: 10, borderRadius: 8, border: '1px solid var(--bdr)', fontSize: 14, fontWeight: 600, background: 'white', color: 'var(--tx)' }}
            />
          </div>

          {/* Remises volume */}
          {(produit.remisesVolume ?? []).length > 0 && (
            <div style={{ background: 'var(--p-soft)', padding: 12, borderRadius: 8, fontSize: 12 }}>
              <div style={{ fontWeight: 600, color: 'var(--p-deep)', marginBottom: 4 }}>💡 Remises volume :</div>
              {produit.remisesVolume!.map((r) => (
                <div key={r.id} style={{ color: 'var(--p-dark)' }}>
                  Dès <strong>{r.quantiteMin} unités</strong> : <strong>-{Number(r.remisePct)}%</strong>
                  {qty >= r.quantiteMin && <CheckCircle2 size={12} style={{ display: 'inline', verticalAlign: '-2px', marginLeft: 6, color: 'var(--success)' }} />}
                </div>
              ))}
            </div>
          )}

          {/* Prix + total */}
          <div style={{ background: 'white', padding: 16, borderRadius: 10, border: '1px solid var(--bdr)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--tx-2)', marginBottom: 4 }}>
              <span>Prix unitaire HT</span>
              <span>{formatFCFA(prixUnitCents)} FCFA</span>
            </div>
            {remiseApplicable > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--success)', marginBottom: 4 }}>
                <span>Remise volume -{remiseApplicable}%</span>
                <span>-{formatFCFA(prixUnitCents - prixApresRemiseCents)} FCFA / u.</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--tx-2)', marginBottom: 4 }}>
              <span>Sous-total {qty} × {formatFCFA(prixApresRemiseCents)}</span>
              <span>{formatFCFA(totalHtCents)} FCFA</span>
            </div>
            {setupCents > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--tx-2)', marginBottom: 4 }}>
                <span>Frais mise en page</span>
                <span>+{formatFCFA(setupCents)} FCFA</span>
              </div>
            )}
            <div style={{ borderTop: '1px solid var(--bdr)', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 700, color: 'var(--p-deep)' }}>
              <span>Total estimé HT</span>
              <span style={{ fontFamily: 'var(--font-d)' }}>{formatFCFA(grandTotalCents)} FCFA</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--tx-3)', marginTop: 4 }}>
              TVA 18% non incluse · frais livraison selon zone Abidjan
            </div>
          </div>

          {/* CTA WhatsApp */}
          <a
            href={whatsappUrl} target="_blank" rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              padding: '16px 24px', borderRadius: 10, background: '#25D366', color: 'white',
              textDecoration: 'none', fontWeight: 700, fontSize: 16, fontFamily: 'var(--font-d)',
              boxShadow: '0 4px 12px rgba(37,211,102,.3)',
            }}
          >
            <MessageCircle size={20} /> Commander par WhatsApp
          </a>
          <div style={{ fontSize: 11, color: 'var(--tx-3)', textAlign: 'center' }}>
            <Truck size={11} style={{ display: 'inline', verticalAlign: '-2px' }} /> Livraison Abidjan 24h · Paiement à la livraison possible
          </div>

          {produit.personnalisable && (
            <div style={{ background: 'var(--p-soft)', padding: 12, borderRadius: 8, fontSize: 12, color: 'var(--p-dark)' }}>
              <Sparkles size={14} style={{ display: 'inline', verticalAlign: '-2px', marginRight: 4 }} />
              <strong>Produit personnalisable.</strong> Délai production : {produit.delaiProductionJours ?? '5-7'} jours ouvrés.
              Envoyez votre logo (PNG 300dpi ou PDF/AI) lors de la commande WhatsApp.
            </div>
          )}
        </div>
      </div>

      <footer style={{ borderTop: '1px solid var(--bdr)', padding: '20px 24px', marginTop: 40, background: 'white', fontSize: 11, color: 'var(--tx-3)', textAlign: 'center' }}>
        TEXTI PRINT SARL · Angré, Abidjan · RCCM CI-ABJ-03-2021-B12-02551
      </footer>
    </div>
  );
};
